import { Component, Inject, OnDestroy, OnInit, computed } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { Subscription, tap } from 'rxjs';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { ITransferProductRequest } from '../../models/product.model';
import { LoadMatchingProducts } from '../../state/product.actions';
import { ProductState } from '../../state/product.state';

export interface TransferProductModalData {
  productId: string;
  productName: string;
  maxQuantity: number;
}

@Component({
  selector: 'app-transfer-product-modal',
  templateUrl: './transfer-product-modal.component.html',
  styleUrls: ['./transfer-product-modal.component.scss'],
  standalone: false,
})
export class TransferProductModalComponent implements OnInit, OnDestroy {
  departmentControl = new FormControl<string | null>(null, [Validators.required]);
  targetProductControl = new FormControl('');
  quantityControl = new FormControl<number | null>(1);
  reasonControl = new FormControl('');

  availableDepartmentsFromStore = select(TenantUserAccessState.getAllAvailableDepartments);
  availableDepartments = computed(() =>
    (this.availableDepartmentsFromStore() ?? []).filter(
      (department) => department.id !== getDepartmentId()
    )
  );

  matchingProducts = select(ProductState.getMatchingProducts);
  targetProducts = computed(() =>
    (this.matchingProducts() ?? []).map((product) => ({
      id: product.id,
      name: `${product.name}${product.articleNo ? ` (${product.articleNo})` : ''}, ${
        product.quantity
      } ${product.quantityType.name} - ${product.menu.name}`,
    }))
  );

  private subscription = new Subscription();

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<TransferProductModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransferProductModalData
  ) {
    if (this.data?.maxQuantity) {
      this.quantityControl.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(this.data.maxQuantity),
      ]);
    }
  }

  ngOnInit(): void {
    this.subscription.add(
      this.departmentControl.valueChanges
        .pipe(
          tap((targetDepartmentId: string | null) => {
            if (targetDepartmentId) {
              this.store.dispatch(
                new LoadMatchingProducts(this.data.productId, targetDepartmentId)
              );
            }
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onTransfer(): void {
    const transferRequest: ITransferProductRequest = {
      sourceStorageProductId: this.data.productId,
      targetDepartmentId: this.departmentControl.value || '',
      quantity: this.quantityControl.value || 0,
      targetStorageProductId: this.targetProductControl.value || '',
      reasonForTransfer: this.reasonControl.value || '',
    };
    this.dialogRef.close(transferRequest);
  }
}
